import { useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { NorthStarsSection } from './components/NorthStarsSection'
import { EngineRoomSection } from './components/EngineRoomSection'
import { HoursAnalyticsSection } from './components/HoursAnalyticsSection'
import { ForecastSection } from './components/ForecastSection'
import { PresalesProcessGoal } from './components/PresalesProcessGoal'
import { SyncButton } from './components/SyncButton'
import { CloseSignalRefreshButton } from './components/CloseSignalRefreshButton'
import { TimePeriodSelector } from './components/TimePeriodSelector'
import { PresalesDealsTable } from './components/PresalesDealsTable'
import { CalendarTab } from './components/CalendarTab'
import { PersonTab } from './components/PersonTab'
import { ToolsTab } from './components/ToolsTab'
import { usePresalesDashboardStore } from './store'
import { usePresalesMembers } from './utils/taskHelpers'

type PresalesDashboardTab = 'performance' | 'operations' | 'calendar' | 'tools' | 'person'

const tabClasses = (isActive: boolean) =>
  `px-3 py-2 text-sm rounded-t-md border border-b-0 border-[var(--border-color)] transition-colors ${
    isActive
      ? 'bg-[var(--bg-secondary)] text-[var(--text-primary)]'
      : 'bg-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
  }`

export function PresalesDashboardPage({ tab, personName: personNameProp }: { tab: PresalesDashboardTab; personName?: string }) {
  const { personName: personNameParam, toolId } = useParams<{ personName: string; toolId: string }>()
  // Prefer the prop (legacy hardcoded routes) but fall back to the URL param
  const personName = personNameProp || personNameParam
  const { loadDeals, loadAllKRs, timePeriod, setTimePeriod, loadGoalStates, eventLinkSummary, fetchEventLinkSummary } = usePresalesDashboardStore()
  const presalesMembers = usePresalesMembers()
  const [opsTab, setOpsTab] = useState<'current' | 'handover'>('current')

  // Load data on mount
  useEffect(() => {
    loadDeals()
    loadAllKRs()
    loadGoalStates()
    fetchEventLinkSummary()
  }, [loadDeals, loadAllKRs, loadGoalStates, fetchEventLinkSummary])

  const staleTaggers = (() => {
    if (!eventLinkSummary || presalesMembers.length === 0) return [] // still loading — don't flash a warning
    const excludeFromAlert = new Set(['Phil', 'Shane']) // not yet actively tagging
    const firstNames = presalesMembers.map(n => n.split(' ')[0])
    const stale: string[] = []
    for (const name of firstNames) {
      if (excludeFromAlert.has(name)) continue
      const personData = eventLinkSummary.by_person.find((p) => p.person === name)
      const last = personData?.last_tagged_at
      if (!last) {
        stale.push(name)
        continue
      }
      const daysSince = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24)
      if (daysSince > 7) stale.push(name)
    }
    return stale
  })()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Presales Dashboard</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Track presales performance, KRs, and pipeline activity
          </p>
        </div>
        {tab === 'performance' && (
          <div className="flex items-center gap-4">
            <TimePeriodSelector value={timePeriod} onChange={setTimePeriod} />
            <SyncButton />
          </div>
        )}
        {tab === 'operations' && (
          <div className="flex items-center gap-3">
            <CloseSignalRefreshButton />
            <SyncButton />
          </div>
        )}
      </div>

      {staleTaggers.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-sm">
          <span className="text-base">⚠️</span>
          <span>{staleTaggers.join(' and ')} {staleTaggers.length === 1 ? 'has' : 'have'}n&apos;t tagged calendar events in the last 7 days. Please tag meetings using the Calendar Tracker extension.</span>
        </div>
      )}

      <div className="flex gap-2 border-b border-[var(--border-color)] overflow-x-auto scrollbar-none">
        <NavLink to="/dashboard/presales/performance" className={({ isActive }) => tabClasses(isActive)}>
          Performance
        </NavLink>
        <NavLink to="/dashboard/presales/operations" className={({ isActive }) => tabClasses(isActive)}>
          Operational overview
        </NavLink>
        <NavLink to="/dashboard/presales/calendar" className={({ isActive }) => tabClasses(isActive)}>
          Calendar
        </NavLink>
        <NavLink to="/dashboard/presales/tools" className={({ isActive }) => tabClasses(isActive)}>
          Tools
        </NavLink>
        <div className="w-px bg-[var(--border-color)] mx-1 my-1" />
        {presalesMembers.map((name) => {
          const slug = name.split(' ')[0].toLowerCase()
          const label = name.split(' ')[0]
          return (
            <NavLink key={slug} to={`/dashboard/presales/${slug}`} className={({ isActive }) => tabClasses(isActive)}>
              {label}
            </NavLink>
          )
        })}
      </div>

      {tab === 'performance' && (
        <div className="space-y-8">
          {/* Section 1: The North Stars — KR1 & KR2 TTM headline metrics */}
          <NorthStarsSection />

          {/* Section 2: The Engine Room — KR3 Capacity & Utilization + KR4 Playbook */}
          <EngineRoomSection />

          {/* Section 3: Hours Reporting — time by person, top customers, value ratio */}
          <HoursAnalyticsSection />

          {/* Section 4: Process Goal — What good looks like */}
          <PresalesProcessGoal />

          {/* Section 5: The Forecast — Cohort analysis & rolling trends */}
          <ForecastSection />
        </div>
      )}

      {tab === 'operations' && (
        <div className="space-y-4">
          {/* Handover split tabs */}
          <div className="flex gap-1 border-b border-[var(--border-color)]">
            <button
              onClick={() => setOpsTab('current')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                opsTab === 'current' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Pre-handover
              {opsTab === 'current' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-color)] rounded-full" />}
            </button>
            <button
              onClick={() => setOpsTab('handover')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                opsTab === 'handover' ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              Handed over to Implementations
              {opsTab === 'handover' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-color)] rounded-full" />}
            </button>
          </div>
          <PresalesDealsTable dealsTab={opsTab} />
        </div>
      )}

      {tab === 'calendar' && <CalendarTab />}

      {tab === 'tools' && <ToolsTab toolId={toolId} />}

      {tab === 'person' && personName && <PersonTab personName={personName} />}
    </div>
  )
}
